import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Button from '@ux/button';
import OkIcon from '@ux/icon/ok';
import InfoIcon from '@ux/icon/information';
import AlertIcon from '@ux/icon/alert';
import Close from '@ux/icon/x';
import { classNames } from '@ux/component-utilities';

/**
 * Mappings of icon name to appropriate icon component and classes.
 *
 * @type {object}
 * @private
 */
const ICONS = {
  success: {
    component: OkIcon,
    classes: ['success']
  },
  warning: {
    component: InfoIcon,
    classes: ['warning']
  },
  error: {
    component: AlertIcon,
    classes: ['error']
  }
};

/**
 * Component for a message.
 *
 * @displayName Growl Message
 * @class GrowlMessage
 * @public
 */
export class GrowlMessage extends Component {
  renderIcon() {
    const props = this.props;

    if (props.icon in ICONS) {
      const { component: Icon, classes } = ICONS[props.icon];
      return (
        <div className={ classNames('ux-growl-icon', ...classes) }>
          <Icon
            aria-hidden='true'
            width={ 22 }
            height={ 22 }
          />
        </div>
      );
    }
  }

  render() {
    const props = this.props;
    const content = typeof props.content === 'function' ? props.content(props) : props.content;

    return (
      <div
        id={ props.id }
        className='ux-growl-msg'
        onMouseEnter={ props.onMouseEnter }
        onMouseLeave={ props.onMouseLeave }>
        <div className='ux-growl-msg-content'>
          <Button
            aria-label={ props.ariaLabels.close }
            className='ux-growl-msg-close'
            design={ 'stateless' }
            data-action='close'
            onClick={ props.onClose }
            onKeyDown={ props.onCloseButtonKeyDown }
            icon={ <Close /> } />
          { this.renderIcon() }
          <div className='ux-growl-msg-title' role='heading' aria-level='1'>{ props.title }</div>
          <div className='ux-growl-msg-text'>{ content }</div>
        </div>
      </div>
    );
  }
}

/**
 * PropType validation.
 *
 * @type {Object}
 * @public
 */
GrowlMessage.propTypes = {
  /** the unique id of the Message  */
  id: PropTypes.string.isRequired,
  /** The title of the message  */
  title: PropTypes.node.isRequired,
  /** The message content as either a valid node or function. */
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /** The icon for the message valid options: none, success, warning, error */
  icon: PropTypes.oneOf(['none', ...Object.keys(ICONS)]).isRequired,
  /** callback function for when the user closes the message */
  onClose: PropTypes.func.isRequired,
  /** callback function for when mouse enteres the message dom node */
  onMouseEnter: PropTypes.func,
  /** callback function for when mouse leaves the message dom node */
  onMouseLeave: PropTypes.func,
  /** passthrough callback function from button when keydown event happens on button */
  onCloseButtonKeyDown: PropTypes.func,
  /** Screenreader labels for sub-elements */
  ariaLabels: PropTypes.shape({
    /** Label for mobile-view menu close button */
    close: PropTypes.string
  })
};

/**
 * PropType defaults.
 *
 * @type {Object}
 * @private
 */
GrowlMessage.defaultProps = {
  icon: 'none',
  ariaLabels: {
    close: 'Close'
  }
};

export default GrowlMessage;
